<template>
  <VRow class="align-items-center body-2 footer px-0 mx-0">
    <div class="d-flex flex-wrap">
      <VCol
        v-if="passengers"
        class="feature-row align-items-center"
      >
        <VIcon class="mr-1">
          mdi-account-multiple
        </VIcon>
        {{ passengers }}
      </VCol>
      <VCol
        v-if="specialSeats"
        class="feature-row align-items-center"
      >
        <VIcon class="mr-1">
          mdi-wheelchair-accessibility
        </VIcon>
        {{ specialSeats }}
      </VCol>
      <VCol
        v-for="feature in selectedFeatures"
        :key="feature.id"
        class="feature-row align-items-center"
      >
        <SImageByBase64
          class="feature-icon mr-1"
          :src="feature.icon"
          :alt="$t('imageAlt.featureIcon', { name: feature.name })"
        />
        <div class="d-flex flex-column">
          <div class="feature-row">
            {{ feature.name }}
          </div>
          <div class="feature-row textTertiary--text">
            {{ feature.price }}
          </div>
        </div>
      </VCol>
      <VCol
        v-if="trip.animalsTransportation"
        class="feature-row align-items-center mr-1"
      >
        <VIcon class="mr-1">
          mdi-paw
        </VIcon>
        <span>
          {{ $t('features.animals') }}
        </span>
      </VCol>
    </div>
  </VRow>
</template>

<script lang="ts">
export default {
  name: 'Footer',
};
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { getTranslateByStringMap } from '@slg/web-customer-shared/src/utils/translationHelper';
import SImageByBase64 from '@slg/web-customer-shared/src/components/common/SImageByBase64.vue';
import { useI18n } from '../../../../composables';

const props = defineProps({
  trip: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

function formatMoney(value: number) {
  return Number(value || 0).toFixed(2);
}

const selectedFeatures = computed(() => props.trip.selectedFeatures
  .map((selectedFeature: Record<string, any>) => ({
    capacity: selectedFeature.capacity,
    icon: selectedFeature.svgIconBase64,
    price: `
            ${selectedFeature.capacity}
            x €${formatMoney(selectedFeature.price)}
            = €${formatMoney(selectedFeature.capacity * selectedFeature.price)}`,
    name: getTranslateByStringMap(selectedFeature.localizedName),
    id: selectedFeature.vehicleFeatureId,
  }))
  .filter(({ capacity }: { capacity: number }) => !!capacity));

const specialSeats = computed(() => (props.trip.specialSeatsCount
  ? `${props.trip.specialSeatsCount}  ${t('features.wheelchairPlace')}`
  : ''));

const passengers = computed(() => {
  const result = [];
  if (props.trip.adultsCount === 1) {
    result.push(`1 ${t('trips.adult')}`);
  } else if (props.trip.adultsCount > 1) {
    result.push(`${props.trip.adultsCount} ${t('trips.adults')}`);
  }
  if (props.trip.infantsCount === 1) {
    result.push(`1 ${t('trips.infant')}`);
  } else if (props.trip.infantsCount > 1) {
    result.push(`${props.trip.infantsCount} ${t('trips.infants')}`);
  }

  return result.join(', ');
});
</script>

<style lang="scss" scoped>
.footer {
  background: #f5f5f5;
  & div {
    & div {
      width: auto;
      display: flex;
      flex-grow: 0;
    }
  }
}

.feature-icon {
  width: 24px;
  height: 24px;
}

.feature-row {
  white-space: nowrap;
}
</style>
